import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Search, Shield, AlertTriangle, CheckCircle, TrendingUp,
  Users, Droplets, Code, Activity, Lock, ArrowRight, Loader2,
  ExternalLink, Sparkles,
} from 'lucide-react';
import logo from '../assets/images/haythix-logo.png';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import FreeToolsTabs from '../components/FreeToolsTabs';
import Seo from '../components/Seo';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

const RATING_COLORS = {
  excellent: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', ring: '#10b981' },
  good:      { bg: 'bg-blue-500/15',    text: 'text-blue-400',    ring: '#3b82f6' },
  fair:      { bg: 'bg-amber-500/15',   text: 'text-amber-400',   ring: '#f59e0b' },
  poor:      { bg: 'bg-red-500/15',     text: 'text-red-400',     ring: '#ef4444' },
};

const SEVERITY_STYLES = {
  high:   { bg: 'bg-red-500/10 border-red-500/30', icon: 'text-red-400' },
  medium: { bg: 'bg-amber-500/10 border-amber-500/30', icon: 'text-amber-400' },
  low:    { bg: 'bg-emerald-500/10 border-emerald-500/30', icon: 'text-emerald-400' },
};

const FACTOR_ICONS = {
  organicVolume: Activity,
  holderDistribution: Users,
  liquidityDepth: Droplets,
  devBehavior: Code,
  tradingPatterns: TrendingUp,
  contractSafety: Shield,
};

function ScoreRing({ value, rating, size = 120 }) {
  const rc = RATING_COLORS[rating] || RATING_COLORS.fair;
  const r = (size - 10) / 2;
  const c = 2 * Math.PI * r;
  const dash = (value / 100) * c;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#1f2937" strokeWidth="6" />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={rc.ring} strokeWidth="6"
          strokeDasharray={`${dash} ${c}`} strokeLinecap="round" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold text-white">{value}</span>
        <span className={`text-xs font-bold uppercase ${rc.text}`}>{rating}</span>
      </div>
    </div>
  );
}

export default function TokenAnalyzerPage() {
  const { address: urlAddress } = useParams();
  const navigate = useNavigate();
  const [inputAddress, setInputAddress] = useState(urlAddress || '');
  const [network, setNetwork] = useState('ethereum');
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  async function analyze() {
    const addr = inputAddress.trim();
    if (!addr) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/public/analyze/${addr}?network=${network}`);
      const data = await res.json();
      if (data.error) { setError(data.error); setAnalysis(null); }
      else { setAnalysis(data); navigate(`/analyze/${addr}`, { replace: true }); }
    } catch (err) {
      setError('Failed to analyze token. Please try again.');
    }
    setLoading(false);
  }

  const seoPath = urlAddress ? `/analyze/${urlAddress}` : '/analyze';
  const seoTitle = urlAddress
    ? `Token health analysis — ${urlAddress.slice(0, 8)}…${urlAddress.slice(-6)}`
    : 'Free Token Health Analyzer — detect rug risks & bot volume';
  const seoDescription = urlAddress
    ? `Free on-chain analysis for token ${urlAddress.slice(0, 10)}… on ${network}. Bot volume detection, holder distribution, liquidity quality, rug-pull signals — by haythix.`
    : 'Paste any token contract address (15 chains supported) for instant rug-pull risk analysis: bot volume detection, holder concentration, liquidity quality. Free, no sign-up required.';

  return (
    <div className="min-h-screen bg-dark-900">
      <Seo title={seoTitle} description={seoDescription} path={seoPath} />
      <PublicNav />
      <FreeToolsTabs />

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-4 pt-12 pb-8 text-center">
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">Free Token Health Analyzer</h1>
        <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
          Instantly detect bot activity, manipulated volume, and rug risks for any token on 15 chains
        </p>

        {/* Search */}
        <div className="max-w-2xl mx-auto flex flex-col sm:flex-row gap-3 mb-4">
          <input type="text" value={inputAddress} onChange={e => setInputAddress(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && analyze()}
            placeholder="Paste token contract address (0x...)"
            className="flex-1 bg-dark-800 border border-dark-600 rounded-xl px-5 py-3 text-white text-sm font-mono placeholder-gray-600 focus:border-accent-primary focus:outline-none" />
          <select value={network} onChange={e => setNetwork(e.target.value)}
            className="bg-dark-800 border border-dark-600 rounded-xl px-4 py-3 text-white text-sm focus:border-accent-primary focus:outline-none">
            {['ethereum','polygon','bsc','arbitrum','base','avalanche','optimism'].map(n =>
              <option key={n} value={n}>{n.charAt(0).toUpperCase()+n.slice(1)}</option>)}
          </select>
          <button onClick={analyze} disabled={!inputAddress.trim() || loading}
            className="px-8 py-3 rounded-xl text-sm font-semibold bg-accent-primary text-dark-900 hover:bg-accent-primary/90 disabled:opacity-40 transition-all flex items-center justify-center gap-2">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            Analyze
          </button>
        </div>

        <div className="flex items-center justify-center gap-6 text-xs text-gray-500">
          <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-400" /> No signup required</span>
          <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-400" /> 15 chains supported</span>
          <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-400" /> Free forever</span>
        </div>
      </section>

      {error && (
        <div className="max-w-3xl mx-auto px-4 mb-6">
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3 text-sm text-red-400 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> {error}
          </div>
        </div>
      )}

      {/* Results */}
      {analysis && (
        <section className="max-w-5xl mx-auto px-4 pb-12 space-y-6">
          {(() => {
            const isNotFound  = analysis.status === 'not_found';
            const isInvalid   = analysis.status === 'invalid' || analysis.status === 'unsupported_chain';
            const hasScore    = typeof analysis.healthScore === 'number';
            const info        = analysis.tokenInfo || {};
            const market      = analysis.marketData || {};
            const factors     = analysis.factors || [];
            const pendingFactors = analysis.pendingFactors || [];
            const risks       = analysis.risks || [];

            if (isInvalid) {
              return (
                <div className="bg-red-500/5 border border-red-500/20 rounded-2xl px-5 py-6 text-center">
                  <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-3" />
                  <p className="text-red-300 font-semibold">{analysis.error || 'Invalid request'}</p>
                </div>
              );
            }
            if (isNotFound) {
              return (
                <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl px-5 py-6">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-amber-300 font-semibold">No liquidity pairs found</p>
                      <p className="text-xs text-amber-300/80 mt-1 leading-relaxed">{analysis.reason}</p>
                      <p className="text-xs text-gray-500 mt-2 font-mono">{analysis.tokenAddress}</p>
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <>
                {/* Score card */}
                <div className="bg-dark-800 rounded-2xl p-8 border border-dark-700/50">
                  <div className="flex flex-col md:flex-row items-center gap-8">
                    {hasScore ? (
                      <ScoreRing value={analysis.healthScore} rating={analysis.rating} size={140} />
                    ) : (
                      <div className="w-[140px] h-[140px] rounded-full border-2 border-dashed border-amber-500/40 flex items-center justify-center">
                        <span className="text-2xl text-amber-400">—</span>
                      </div>
                    )}
                    <div className="flex-1 text-center md:text-left">
                      <h2 className="text-xl font-bold text-white mb-1">
                        {info.name ? `${info.name} (${info.symbol})` : `Health Score: ${analysis.healthScore}/100`}
                      </h2>
                      {info.priceUsd != null && (
                        <p className="text-2xl font-bold text-emerald-400 mb-1">
                          ${info.priceUsd < 0.01 ? info.priceUsd.toExponential(3) : info.priceUsd.toLocaleString(undefined, { maximumFractionDigits: 6 })}
                        </p>
                      )}
                      <p className="text-gray-400 text-sm mb-3 font-mono break-all">{analysis.tokenAddress}</p>
                      <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                        <span className={`text-xs font-bold px-3 py-1 rounded-full uppercase ${
                          hasScore ? (RATING_COLORS[analysis.rating]?.bg + ' ' + RATING_COLORS[analysis.rating]?.text)
                                   : 'bg-amber-500/15 text-amber-400'
                        }`}>
                          {hasScore ? analysis.rating : 'pending'}
                        </span>
                        <span className="text-xs px-3 py-1 rounded-full bg-dark-700 text-gray-400 capitalize">{analysis.chain || analysis.network}</span>
                        {info.dex && <span className="text-xs px-3 py-1 rounded-full bg-dark-700 text-gray-400">{info.dex}</span>}
                        {info.dexUrl && (
                          <a href={info.dexUrl} target="_blank" rel="noopener noreferrer"
                            className="text-xs px-3 py-1 rounded-full bg-violet-500/15 text-violet-300 hover:bg-violet-500/25 transition-colors flex items-center gap-1">
                            DexScreener <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Market data — real DexScreener numbers */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-dark-800 rounded-xl p-4 border border-dark-700/50">
                    <p className="text-[10px] text-gray-500 uppercase tracking-wide">Liquidity</p>
                    <p className="text-lg font-bold text-white mt-1">
                      {market.liquidityUsd != null ? `$${Math.round(market.liquidityUsd).toLocaleString()}` : '—'}
                    </p>
                  </div>
                  <div className="bg-dark-800 rounded-xl p-4 border border-dark-700/50">
                    <p className="text-[10px] text-gray-500 uppercase tracking-wide">24h Volume</p>
                    <p className="text-lg font-bold text-white mt-1">
                      {market.volume24hUsd != null ? `$${Math.round(market.volume24hUsd).toLocaleString()}` : '—'}
                    </p>
                  </div>
                  <div className="bg-dark-800 rounded-xl p-4 border border-dark-700/50">
                    <p className="text-[10px] text-gray-500 uppercase tracking-wide">FDV</p>
                    <p className="text-lg font-bold text-white mt-1">
                      {market.fdvUsd != null ? `$${Math.round(market.fdvUsd).toLocaleString()}` : '—'}
                    </p>
                  </div>
                  <div className="bg-dark-800 rounded-xl p-4 border border-dark-700/50">
                    <p className="text-[10px] text-gray-500 uppercase tracking-wide">24h Price</p>
                    <p className={`text-lg font-bold mt-1 ${market.priceChange24h >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {market.priceChange24h != null ? `${market.priceChange24h >= 0 ? '+' : ''}${market.priceChange24h.toFixed(1)}%` : '—'}
                    </p>
                  </div>
                  <div className="bg-dark-800 rounded-xl p-4 border border-dark-700/50">
                    <p className="text-[10px] text-gray-500 uppercase tracking-wide">24h Buys</p>
                    <p className="text-lg font-bold text-emerald-400 mt-1">{(market.buys24h || 0).toLocaleString()}</p>
                  </div>
                  <div className="bg-dark-800 rounded-xl p-4 border border-dark-700/50">
                    <p className="text-[10px] text-gray-500 uppercase tracking-wide">24h Sells</p>
                    <p className="text-lg font-bold text-red-400 mt-1">{(market.sells24h || 0).toLocaleString()}</p>
                  </div>
                  <div className="bg-dark-800 rounded-xl p-4 border border-dark-700/50">
                    <p className="text-[10px] text-gray-500 uppercase tracking-wide">Pairs Found</p>
                    <p className="text-lg font-bold text-white mt-1">{market.pairsCount || 0}</p>
                  </div>
                  <div className="bg-dark-800 rounded-xl p-4 border border-dark-700/50">
                    <p className="text-[10px] text-gray-500 uppercase tracking-wide">Quote</p>
                    <p className="text-lg font-bold text-white mt-1">{info.quoteSymbol || '—'}</p>
                  </div>
                </div>

                {/* Real factor breakdown */}
                <div>
                  <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3">Health Factors</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {factors.map((factor) => {
                      const score = factor.score;
                      const has = typeof score === 'number';
                      const color = !has ? 'text-gray-500' :
                        score >= 70 ? 'text-emerald-400' : score >= 45 ? 'text-amber-400' : 'text-red-400';
                      const barColor = score >= 70 ? 'bg-emerald-400' : score >= 45 ? 'bg-amber-400' : 'bg-red-400';
                      return (
                        <div key={factor.label} className="bg-dark-800 rounded-xl p-5 border border-dark-700/50">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-sm text-white font-medium">{factor.label}</span>
                            <span className={`ml-auto text-lg font-bold ${color}`}>{has ? score : '—'}</span>
                          </div>
                          <div className="w-full h-2 bg-dark-700 rounded-full mb-2">
                            {has && <div className={`h-full rounded-full ${barColor}`} style={{ width: `${score}%` }} />}
                          </div>
                          {factor.displayValue && (
                            <p className="text-[10px] text-gray-500 mt-1">{factor.displayValue}</p>
                          )}
                          {factor.reason && !has && (
                            <p className="text-[10px] text-amber-400/70 mt-1">{factor.reason}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Pending factors (honest "coming in v1.1") */}
                {pendingFactors.length > 0 && (
                  <div>
                    <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                      Deeper Analysis <span className="text-[9px] bg-amber-500/15 text-amber-400 px-2 py-0.5 rounded-full">COMING IN v1.1</span>
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {pendingFactors.map(pf => (
                        <div key={pf.key} className="bg-dark-800/50 rounded-xl p-4 border border-dashed border-dark-600">
                          <p className="text-sm text-white font-medium">{pf.label}</p>
                          <p className="text-xs text-gray-500 mt-1">{pf.desc}</p>
                          <p className="text-[10px] text-amber-400/70 mt-2">{pf.reason}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Risk flags from real factors */}
                {risks.length > 0 && (
                  <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
                    <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">
                      Risk Flags ({risks.length})
                    </h3>
                    <div className="space-y-2">
                      {risks.map((risk, i) => {
                        const ss = SEVERITY_STYLES[risk.severity] || SEVERITY_STYLES.medium;
                        return (
                          <div key={i} className={`flex items-start gap-3 rounded-xl px-4 py-3 border ${ss.bg}`}>
                            <AlertTriangle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${ss.icon}`} />
                            <div>
                              <p className="text-sm text-white font-medium">{risk.factor}</p>
                              <p className="text-xs text-gray-400 mt-0.5">{risk.message}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

          {/* Conversion CTA */}
          <div className="bg-gradient-to-r from-accent-primary/10 to-amber-500/10 border border-accent-primary/30 rounded-2xl p-8 text-center">
            <Sparkles className="w-8 h-8 text-accent-primary mx-auto mb-3" />
            <h3 className="text-xl font-bold text-white mb-2">Want continuous monitoring?</h3>
            <p className="text-gray-400 text-sm mb-5 max-w-md mx-auto">
              Get real-time alerts when this token's health score changes, compare against competitors, and access AI-powered risk analysis.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to={`/register?ref=analyzer&token=${analysis.tokenAddress}`}
                className="px-6 py-3 rounded-xl text-sm font-semibold bg-accent-primary text-dark-900 hover:bg-accent-primary/90 transition-all flex items-center justify-center gap-2">
                Start Free Trial <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/pricing"
                className="px-6 py-3 rounded-xl text-sm font-medium text-gray-400 border border-dark-600 hover:text-white hover:border-dark-500 transition-all">
                View Pricing
              </Link>
            </div>
          </div>
              </>
            );
          })()}
        </section>
      )}

      {/* Empty state — before search */}
      {!analysis && !loading && !error && (
        <section className="max-w-4xl mx-auto px-4 pb-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
            {[
              { icon: Shield, title: 'Bot Detection', desc: 'Identify wash trading and automated volume manipulation' },
              { icon: Users, title: 'Holder Analysis', desc: 'Detect concentrated holdings and whale risk' },
              { icon: Activity, title: 'Risk Scoring', desc: '6-factor health score with actionable risk flags' },
            ].map((f, i) => (
              <div key={i} className="bg-dark-800 rounded-xl p-6 border border-dark-700/50 text-center">
                <f.icon className="w-8 h-8 text-accent-primary mx-auto mb-3" />
                <h3 className="text-white font-semibold mb-1">{f.title}</h3>
                <p className="text-xs text-gray-500">{f.desc}</p>
              </div>
            ))}
          </div>

          {/* CTA for non-searched users */}
          <div className="text-center">
            <p className="text-gray-500 text-sm mb-4">Want more than one-time analysis?</p>
            <Link to="/register" className="px-6 py-3 rounded-xl text-sm font-semibold bg-accent-primary text-dark-900 hover:bg-accent-primary/90 transition-all inline-flex items-center gap-2">
              Create Free Account <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      )}

      {/* SEO FAQ — these answer the most-searched queries around free token analyzers */}
      <section className="max-w-3xl mx-auto px-4 py-12">
        <h2 className="text-2xl font-bold text-white mb-6">Frequently asked questions</h2>
        <div className="space-y-4 text-sm">
          <details className="group rounded-xl border border-dark-700/50 bg-dark-800/40 p-4">
            <summary className="font-semibold text-white cursor-pointer">What does the Token Health Analyzer detect?</summary>
            <p className="text-gray-300 leading-relaxed mt-3">
              Holder concentration, liquidity quality, suspicious volume patterns (wash trading / bot activity),
              contract risk signals (mint authority, blacklist functions), and basic price metadata. It runs across
              15 chains: Ethereum, BSC, Polygon, Arbitrum, Base, Avalanche, Optimism, Solana, Cronos, Fantom,
              zkSync Era, Mantle, Linea, Scroll, Blast.
            </p>
          </details>
          <details className="group rounded-xl border border-dark-700/50 bg-dark-800/40 p-4">
            <summary className="font-semibold text-white cursor-pointer">Is the Token Analyzer free?</summary>
            <p className="text-gray-300 leading-relaxed mt-3">
              Yes — anyone can paste a contract address and get the analysis with no account required. The free
              version returns the headline risk score and the major signals. Logged-in users on a paid plan get
              deeper rug-pull forensics, live holder change tracking, and the full Token Launch Scanner inside the
              app.
            </p>
          </details>
          <details className="group rounded-xl border border-dark-700/50 bg-dark-800/40 p-4">
            <summary className="font-semibold text-white cursor-pointer">How accurate are token rug-pull detectors?</summary>
            <p className="text-gray-300 leading-relaxed mt-3">
              No detector is 100% accurate — anyone claiming otherwise is selling something. Our analyzer flags
              signals (concentration, mint authority, low liquidity, wash-trading patterns) but cannot predict
              human intent. PROBABLY safe is never a green light. Pair the analyzer with your own research before
              committing capital.
            </p>
          </details>
          <details className="group rounded-xl border border-dark-700/50 bg-dark-800/40 p-4">
            <summary className="font-semibold text-white cursor-pointer">Can I check tokens on Solana?</summary>
            <p className="text-gray-300 leading-relaxed mt-3">
              Yes. Solana uses base58 addresses (no 0x prefix). Paste the SPL mint address and pick Solana from the
              chain selector. EVM chains use 0x + 40 hex character format.
            </p>
          </details>
          <details className="group rounded-xl border border-dark-700/50 bg-dark-800/40 p-4">
            <summary className="font-semibold text-white cursor-pointer">How does haythix Token Analyzer compare to DexTools or DexScreener?</summary>
            <p className="text-gray-300 leading-relaxed mt-3">
              DexTools and DexScreener are excellent analytics dashboards but read-only — you cannot trade from
              them. haythix combines analyzer + execution: when a token passes your safety review, you can run a
              Smart Trade, Grid, or DCA on the same address without leaving the platform.
            </p>
          </details>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
